import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, MessageCircle } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { CONTACT } from "@/lib/contact";

export default function Footer() {
  return (
    <footer className="relative section-bg-deep border-t border-gold-500/10 pt-20 pb-10" data-testid="footer">
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold-500/40 to-transparent" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="PB-Lux" className="h-14 w-14 rounded-full border border-gold-500/30" />
              <div>
                <p className="font-display text-2xl text-gold-200 leading-none">PB-LUX</p>
                <p className="text-[10px] tracking-[0.3em] text-gold-500/80 uppercase">Vehicle Rental — Est. 2023</p>
              </div>
            </div>
            <p className="text-sm text-foreground/60 leading-relaxed max-w-md">
              Palawan's premier luxury vehicle rental — a curated fleet of cars, SUVs, vans, and scooters,
              ready to take you on the journey of a lifetime through Puerto Princesa and beyond.
            </p>
            <div className="gold-divider w-32 my-6" />
            <div className="space-y-3 text-sm text-foreground/70">
              <a href="https://maps.google.com/?q=Puerto+Princesa+Palawan" target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-gold-300 transition-colors group">
                <MapPin className="w-4 h-4 text-gold-500 mt-1 flex-shrink-0" />
                <span>Puerto Princesa, Palawan, Philippines</span>
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-gold-300 transition-colors">
                <Mail className="w-4 h-4 text-gold-500 flex-shrink-0" />
                {CONTACT.email}
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-3 hover:text-gold-300 transition-colors">
                <Phone className="w-4 h-4 text-gold-500 flex-shrink-0" />
                {CONTACT.phone}
              </a>
              <a href={`https://wa.me/${CONTACT.whatsappRaw.replace("+", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-gold-300 transition-colors">
                <MessageCircle className="w-4 h-4 text-gold-500 flex-shrink-0" />
                WhatsApp / Viber: {CONTACT.whatsapp}
              </a>
            </div>
          </div>

          {/* Explore */}
          <div className="md:col-span-3">
            <h4 className="text-xs font-sans-display tracking-[0.25em] text-gold-500 uppercase mb-5">Explore</h4>
            <ul className="space-y-3 text-sm text-foreground/70">
              <li><Link to="/" className="hover:text-gold-300 transition">Home</Link></li>
              <li><Link to="/fleet" className="hover:text-gold-300 transition">Browse Fleet</Link></li>
              <li><Link to="/fleet?type=car" className="hover:text-gold-300 transition">Cars & SUVs</Link></li>
              <li><Link to="/fleet?type=motorbike" className="hover:text-gold-300 transition">Motorbikes</Link></li>
              <li><Link to="/about" className="hover:text-gold-300 transition">About Us</Link></li>
              <li><Link to="/my-bookings" className="hover:text-gold-300 transition">My Bookings</Link></li>
            </ul>
          </div>

          {/* Booking info */}
          <div className="md:col-span-4">
            <h4 className="text-xs font-sans-display tracking-[0.25em] text-gold-500 uppercase mb-5">Booking Info</h4>
            <ul className="space-y-3 text-sm text-foreground/70">
              <li>• Reservation fee = one day's rental of the unit (deductible from total)</li>
              <li>• Driver's License + 1 valid ID required</li>
              <li>• Collateral your choice: leave 1 valid ID OR refundable cash deposit — Motorbike ₱2,000 · Small car ₱3,000 · Big car ₱5,000</li>
              <li>• Duration discounts: 3+ days 7% · 7+ days 9% · 15+ days 14% · 30+ days 18%</li>
              <li>• El Nido pickup & drop-off available for cars (fee by vehicle class)</li>
              <li>• Minimum 2 days for outside-city drives</li>
              <li>• Full payment prior to release</li>
            </ul>
            <div className="mt-6 flex gap-3">
              <a href={CONTACT.facebookUrl} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-gold-500/30 flex items-center justify-center hover:bg-gold-500/10 transition" data-testid="footer-facebook">
                <Facebook className="w-4 h-4 text-gold-300" />
              </a>
              <a href={`https://wa.me/${CONTACT.whatsappRaw.replace("+", "")}`} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-gold-500/30 flex items-center justify-center hover:bg-gold-500/10 transition" data-testid="footer-whatsapp">
                <MessageCircle className="w-4 h-4 text-gold-300" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-gold-500/10 text-center md:text-left" data-testid="footer-assistance">
          <p className="text-sm text-foreground/70">
            <span className="text-gold-200 font-medium">Need assistance with your booking?</span>{" "}
            For the fastest response, please email us or message us on WhatsApp — our team will assist you with your vehicle rental inquiry and reservation.
          </p>
          <div className="mt-3 flex flex-wrap gap-3 justify-center md:justify-start text-sm">
            <a href={`mailto:${CONTACT.email}`} className="text-gold-300 hover:text-gold-100 underline underline-offset-4" data-testid="footer-email-link">{CONTACT.email}</a>
            <a href={`https://wa.me/${CONTACT.whatsappRaw.replace("+", "")}`} target="_blank" rel="noreferrer" className="text-green-300 hover:text-green-200 underline underline-offset-4" data-testid="footer-whatsapp-link">WhatsApp {CONTACT.whatsapp}</a>
            <Link to="/insurance-policy" className="text-foreground/60 hover:text-gold-300 underline underline-offset-4" data-testid="footer-insurance-link">Insurance & Damage Policy</Link>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-gold-500/10" data-testid="footer-payments">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold-500/80 mb-4 text-center md:text-left">We Accept</p>
          <div className="flex flex-wrap gap-2.5 justify-center md:justify-start">
            {["GCash", "Maya", "BPI", "BDO", "PayPal", "Visa / Mastercard", "Cash"].map((p) => (
              <span key={p} className="px-4 py-1.5 rounded-full border border-gold-500/25 bg-ink-800/60 text-xs text-foreground/70 font-sans-display tracking-wider hover:border-gold-500/50 hover:text-gold-200 transition-colors">
                {p}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-gold-500/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <p className="text-xs text-foreground/40 font-sans-display tracking-wider">
              © {new Date().getFullYear()} PB-LUX Vehicle Rental. All rights reserved.
            </p>
            <p className="text-[10px] text-foreground/35 mt-1 font-sans-display tracking-wider">
              <Link to="/about#credentials-section" className="hover:text-gold-300 transition">DTI Reg. No. 6060574 · Mayor's Permit BP-2026-08271-0 · Puerto Princesa City</Link>
            </p>
          </div>
          <Link to="/admin" className="text-[10px] uppercase tracking-[0.3em] text-foreground/30 hover:text-gold-500 transition" data-testid="footer-admin-link">
            Admin Portal
          </Link>
        </div>
      </div>
    </footer>
  );
}
